import { isString } from "#src/utils/is";
import { cloneElement, isValidElement } from "react";

import type { MenuItemType } from "./types";

/**
 * Converts all labels in the menu tree to internationalized text
 * @param menus Original menu array
 * @param t Translation function
 * @returns Converted menu array
 */
export function translateMenus(menus: MenuItemType[], t: (key: string) => string): MenuItemType[] {
	return menus.map((menu) => {
		let translatedLabel: React.ReactNode = menu.label;
		if (isValidElement(menu.label)) {
			translatedLabel = cloneElement(menu.label, {}, t(menu.label.props.children));
		}
		if (isString(menu.label)) {
			translatedLabel = t(menu.label);
		}
		const translatedMenu = {
			...menu,
			label: translatedLabel,
		};

		if (menu.children && menu.children.length > 0) {
			translatedMenu.children = translateMenus(menu.children, t);
		}

		return translatedMenu;
	});
}

/**
 * Find menu by path
 *
 * @param list Menu list
 * @param path Menu path
 * @returns Found menu object, returns null if not found
 */
export function findMenuByPath(
	list: MenuItemType[],
	path?: string,
): MenuItemType | null {
	for (const menu of list) {
		if (menu.key === path) {
			return menu;
		}
		const findMenu = menu.children && findMenuByPath(menu.children, path);
		if (findMenu) {
			return findMenu;
		}
	}
	return null;
}

/**
 * Find root menu by path
 *
 * @param menus Menu list
 * @param path Menu path, optional
 * @returns Object containing the found menu, root menu, and root menu path
 */
export function findRootMenuByPath(menus: MenuItemType[], path?: string): {
	findMenu: MenuItemType | null
	rootMenu: MenuItemType | null
	rootMenuPath: string | null
} {
	// Initialize return value
	let findMenu: MenuItemType | null = null;
	let rootMenu: MenuItemType | null = null;
	let rootMenuPath: string | null = null;

	// Return default values if no path is provided
	if (!path) {
		return {
			findMenu: null,
			rootMenu: null,
			rootMenuPath: null,
		};
	}

	// Recursive search function
	const find = (
		list: MenuItemType[],
		targetPath: string,
		parents: MenuItemType[] = [],
	): boolean => {
		for (const menu of list) {
			// If the target menu is found
			if (menu.key === targetPath) {
				findMenu = menu;
				// If there is no parent menu, it means the current menu is the root menu
				if (parents.length === 0) {
					rootMenu = menu;
					rootMenuPath = menu.key;
				} else {
					// Get the top-level parent menu
					rootMenu = parents[0];
					rootMenuPath = parents[0].key;
				}
				return true;
			}

			// If there are child menus, continue the recursive search
			if (menu.children && menu.children.length > 0) {
				// Add the current menu to the parent menu array
				const found = find(menu.children, targetPath, [...parents, menu]);
				if (found) {
					return true;
				}
			}
		}
		return false;
	};

	// Start searching
	find(menus, path);

	return {
		findMenu,
		rootMenu,
		rootMenuPath,
	};
}

/**
 * Recursively find the deepest first menu item under the first child menu path
 *
 * @param splitSideNavItems Menu list
 * @returns The deepest first menu item found
 */
export function findDeepestFirstItem(splitSideNavItems: MenuItemType[]): MenuItemType | null {
	// Return null if the list is empty
	if (!splitSideNavItems || splitSideNavItems.length === 0) {
		return null;
	}

	// Get the first menu item
	const firstItem = splitSideNavItems[0];

	// If the current item has child menus, continue the recursive search
	if (firstItem.children && firstItem.children.length > 0) {
		return findDeepestFirstItem(firstItem.children);
	}

	// If there are no child menus, it means the bottom level is reached, return the current item
	return firstItem;
}

/**
 * Get all keys and their corresponding levels from menu items
 *
 * @param menuItems1 Menu item array
 * @returns An object where the key is the menu item's key, and the value is the menu item's level
 */
export function getLevelKeys(menuItems1: MenuItemType[]) {
	const key: Record<string, number> = {};
	const func = (menuItems2: MenuItemType[], level = 1) => {
		menuItems2.forEach((item) => {
			if (item.key) {
				key[item.key] = level;
			}
			if (item.children) {
				func(item.children, level + 1);
			}
		});
	};
	func(menuItems1);
	return key;
};

/**
 * Get the parent keys of the menu items
 *
 * @param menuItems Menu item array
 * @returns Returns an object recording the array of parent keys corresponding to each menu item's key
 */
export function getParentKeys(menuItems: MenuItemType[]): Record<string, string[]> {
	const parentKeyMap: Record<string, string[]> = {};

	function traverse(items: MenuItemType[], parentKeys: string[] = []) {
		for (const item of items) {
			// Record the array of parent keys for the current key
			parentKeyMap[item.key] = [...parentKeys];

			// If there are child nodes, traverse recursively
			if (Array.isArray(item.children) && item.children.length) {
				traverse(item.children, [...parentKeys, item.key]);
			}
		}
	}

	traverse(menuItems);
	return parentKeyMap;
}
